import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import SectionGlow from "@/components/site/SectionGlow";

// Inline {gold:...} token renderer (mirrors the site-wide RichText grammar).
const renderTitle = (text) => {
    if (!text) return null;
    const parts = [];
    const re = /\{gold:([^}]+)\}/g;
    let lastIndex = 0;
    let key = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
        parts.push(<span key={key++} className="pg-text-gold-static">{match[1]}</span>);
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
    return parts;
};

/**
 * PageHero — the airy, sunlit page header shared by every dedicated page.
 * Gives each route its own identity (breadcrumb + eyebrow + H1 + intro) while
 * staying part of one system. Generous top padding clears the fixed nav.
 */
export const PageHero = ({ crumbs = [], eyebrow, title, intro, tone = "warm", children }) => {
    const reduce = useReducedMotion();
    return (
        <header
            className="pg-page-hero pg-section relative pt-32 sm:pt-36 lg:pt-40"
            data-testid="page-hero"
            style={{ paddingBottom: "clamp(2.5rem, 5vw, 4rem)" }}
        >
            <SectionGlow tone={tone} />
            <div className="pg-container relative z-[1]">
                {crumbs.length > 0 && (
                    <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5" data-testid="page-breadcrumb">
                        {crumbs.map((c, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5">
                                {c.to ? (
                                    <Link
                                        to={c.to}
                                        className="pg-link-underline font-mono text-[11px] uppercase tracking-[0.2em]"
                                        style={{ color: "var(--pg-fg-muted)" }}
                                    >
                                        {c.label}
                                    </Link>
                                ) : (
                                    <span className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--pg-fg)" }}>
                                        {c.label}
                                    </span>
                                )}
                                {i < crumbs.length - 1 && (
                                    <ChevronRight className="h-3 w-3" style={{ color: "var(--pg-fg-muted)" }} strokeWidth={1.6} />
                                )}
                            </span>
                        ))}
                    </nav>
                )}
                <motion.div
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    {eyebrow && <span className="pg-eyebrow">{eyebrow}</span>}
                    <h1
                        className="pg-h1-page mt-5"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="page-hero-title"
                    >
                        {renderTitle(title)}
                    </h1>
                    {intro && (
                        <p className="pg-lede mt-6 max-w-2xl">
                            {intro}
                        </p>
                    )}
                    {children && <div className="mt-8">{children}</div>}
                </motion.div>
            </div>
        </header>
    );
};

export default PageHero;
